import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePermissions } from '@/features/auth';
import { useSelectedCountry } from '@/features/country-scope';
import { DateRangePicker, lastNDays } from '@/shared/components/date-range-picker';
import { ErrorState } from '@/shared/components/error-state';
import { ExportActions } from '@/shared/components/export-actions';
import { PageContainer } from '@/shared/components/page-container';
import { PageHeader } from '@/shared/components/page-header';

import {
  exportChannelDailyReport,
  exportPartnerShareReport,
  exportReport,
  type ReportKind,
} from '../api/reports.api';
import {
  useChannelDailyReport,
  useFinancialReport,
  useOperationalReport,
  usePartnerShareReport,
  usePricingReport,
  useProviderReport,
} from '../api/reports.queries';
import {
  CatalogueByCountry,
  CountryComparison,
  PricingTables,
  ProviderTables,
} from '../components/insight-tables';
import { FinancialTab } from '../components/financial-tab';
import { ReportNoData } from '../components/report-no-data';
import { OperationalTables } from '../components/report-tables';
import { ReportsFilters } from '../components/reports-filters';
import {
  CLEAR_REPORT_FILTERS,
  hasReportFilters,
  readReportFilters,
  type ReportFilterChanges,
} from '../lib/report-filters';
import {
  FINANCIAL_VIEW_PARAM,
  isPartnerFinancialView,
  readFinancialView,
  usesFinancialReport,
  type FinancialView,
} from '../lib/financial-views';
import { GROUPINGS, GROUPING_LABEL } from '../lib/report-labels';
import type { ReportGrouping, ReportParams } from '../types/report.types';

const ALL = 'all';

type Tab = 'operational' | 'financial' | 'pricing' | 'providers' | 'countries';

/**
 * Reports (BR §7.8): operational (reports.operations.read), financial
 * (reports.financial.read), pricing (reports.pricing.read), providers &
 * catalogue (reports.providers.read / reports.catalogue.read) and a country
 * comparison. Each tab is shown only with its permission; the Partner
 * (reports.partner.read) gets a Financial tab of its own share's totals and the
 * transactions without cost. Filters — period,
 * country (the selected one or all of mine), payment channel, provider, brand,
 * denomination, grouping, the open tab and the Financial sub-tab (`fin`) — live
 * in the URL so a report can be shared, and every tab and export uses the same
 * filters. The Financial tab exports its open sub-tab where it has its own export.
 */
export function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = usePermissions();
  const { country, countries } = useSelectedCountry();
  const canFinancial = can('reports.financial.read');
  const partnerFinancial = !canFinancial && can('reports.partner.read');
  const canPricing = can('reports.pricing.read');
  const canProviders = can('reports.providers.read');
  const canCatalogue = can('reports.catalogue.read') && can('catalogue.read');
  const allowed: Record<Tab, boolean> = {
    operational: true,
    financial: canFinancial || partnerFinancial,
    pricing: canPricing,
    providers: canProviders || canCatalogue,
    countries: true,
  };
  const fallback = useMemo(() => lastNDays(30), []);

  const range = {
    from: searchParams.get('from') ?? fallback.from,
    to: searchParams.get('to') ?? fallback.to,
  };
  const requestedGroup = searchParams.get('group') as ReportGrouping | null;
  const groupBy: ReportGrouping =
    requestedGroup && GROUPINGS.includes(requestedGroup) ? requestedGroup : 'day';
  const allCountries = searchParams.get('scope') === ALL;
  const requestedTab = searchParams.get('tab') as Tab | null;
  const tab: Tab = requestedTab && allowed[requestedTab] ? requestedTab : 'operational';
  // Every financial sub-tab needs only reports.financial.read; the Partner sees two of them.
  const financialView: FinancialView = readFinancialView(
    searchParams.get(FINANCIAL_VIEW_PARAM),
    partnerFinancial ? isPartnerFinancialView : undefined,
  );
  const onFinancial = (view: FinancialView) => tab === 'financial' && financialView === view;

  const filters = readReportFilters(searchParams);
  const filtered = hasReportFilters(filters);
  const countryId = allCountries ? undefined : country?.id;

  const params: ReportParams = { from: range.from, to: range.to, groupBy, countryId, ...filters };
  // The comparison is always every country in scope, grouped by country.
  const comparison: ReportParams = {
    from: range.from,
    to: range.to,
    groupBy: 'country',
    ...filters,
  };
  const operational = useOperationalReport(
    tab === 'countries' ? comparison : params,
    tab === 'operational' || tab === 'countries',
  );
  const financial = useFinancialReport(
    tab === 'countries' ? comparison : params,
    (tab === 'financial' && canFinancial && usesFinancialReport(financialView)) ||
      (tab === 'countries' && canFinancial),
  );
  // The sub-tabs fetch these themselves; the page reads the same cache for the export row count.
  const channelDaily = useChannelDailyReport(params, onFinancial('channels'));
  const partnerShare = usePartnerShareReport(params, onFinancial('partners'));
  const pricing = usePricingReport(params, tab === 'pricing');
  const providers = useProviderReport(params, tab === 'providers' && canProviders);
  const exportKind: ReportKind = tab === 'countries' ? 'operational' : tab;
  const exportParams = tab === 'countries' ? comparison : params;
  // The Financial tab exports its open sub-tab where it has an export of its own.
  const excel =
    tab === 'providers' && !canProviders
      ? undefined
      : onFinancial('channels')
        ? () => exportChannelDailyReport(params)
        : onFinancial('partners')
          ? () => exportPartnerShareReport(params)
          : () => exportReport(exportKind, exportParams);
  const pdfList = onFinancial('channels')
    ? 'report-channels-daily'
    : onFinancial('partners')
      ? 'report-partner-share'
      : `report-${tab}`;
  const pdfGroupBy = onFinancial('channels')
    ? 'day'
    : onFinancial('partners')
      ? exportParams.groupBy === 'month'
        ? 'month'
        : 'day'
      : exportParams.groupBy;
  const rowCount = onFinancial('channels')
    ? channelDaily.data?.rows.length
    : onFinancial('partners')
      ? partnerShare.data?.rows.length
      : tab === 'financial'
        ? financial.data?.rows.length
        : tab === 'pricing'
          ? pricing.data?.rows.length
          : tab === 'providers'
            ? providers.data?.rows.length
            : operational.data?.rows.length;

  // One URL update per change: several keys written in two calls would lose one.
  const update = useCallback(
    (changes: Record<string, string | null | undefined>) =>
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const [key, value] of Object.entries(changes)) {
            if (value === undefined) continue;
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  // A change to which orders are in the report returns the transactions to their first page.
  const applyFilters = useCallback(
    (changes: ReportFilterChanges) => update({ ...changes, txPage: null }),
    [update],
  );
  const pdfFilters: Record<string, string> = {
    from: range.from,
    to: range.to,
    groupBy: pdfGroupBy,
  };
  if (filters.channel) pdfFilters.channel = filters.channel;
  if (filters.providerId) pdfFilters.providerId = String(filters.providerId);
  if (filters.brandId) pdfFilters.brandId = String(filters.brandId);
  if (filters.denominationId) pdfFilters.denominationId = String(filters.denominationId);

  return (
    <PageContainer>
      <PageHeader
        title="Reports"
        description="What happened in the period, from each order as it was at the time."
        actions={
          <span className="flex flex-wrap items-center gap-2">
            <ExportActions
              excel={excel}
              pdf={{
                list: pdfList,
                rows: rowCount ?? 0,
                countryId: exportParams.countryId,
                filters: pdfFilters,
              }}
            />
            <span data-print="hide">
              <DateRangePicker
                value={range}
                onChange={(value) => update({ from: value.from, to: value.to, txPage: null })}
              />
            </span>
          </span>
        }
      />

      <div className="flex flex-wrap items-center gap-2" data-print="hide">
        <Select
          value={allCountries ? ALL : 'selected'}
          onValueChange={(value) => update({ scope: value === ALL ? ALL : null, txPage: null })}
        >
          <SelectTrigger size="sm" className="h-8 w-48 text-xs" aria-label="Countries">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="selected" className="text-xs">
              {country?.nameEn ?? 'Selected country'}
            </SelectItem>
            <SelectItem value={ALL} className="text-xs">
              All my countries
            </SelectItem>
          </SelectContent>
        </Select>
        <Select value={groupBy} onValueChange={(value) => update({ group: value })}>
          <SelectTrigger size="sm" className="h-8 w-44 text-xs" aria-label="Group by">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GROUPINGS.map((grouping) => (
              <SelectItem key={grouping} value={grouping} className="text-xs">
                By {GROUPING_LABEL[grouping].toLowerCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <ReportsFilters filters={filters} countryId={countryId} onChange={applyFilters} />

      <Tabs value={tab} onValueChange={(value) => update({ tab: value })}>
        <TabsList>
          <TabsTrigger value="operational">Operational</TabsTrigger>
          {allowed.financial && <TabsTrigger value="financial">Financial</TabsTrigger>}
          {canPricing && <TabsTrigger value="pricing">Pricing</TabsTrigger>}
          {allowed.providers && (
            <TabsTrigger value="providers">Providers &amp; catalogue</TabsTrigger>
          )}
          <TabsTrigger value="countries">Countries</TabsTrigger>
        </TabsList>

        <TabsContent value="operational" className="pt-3">
          {operational.isLoading ? (
            <Skeleton className="h-64" />
          ) : operational.error ? (
            <ErrorState error={operational.error} onRetry={() => void operational.refetch()} />
          ) : !operational.data || operational.data.rows.length === 0 ? (
            <ReportNoData filtered={filtered} onClear={() => applyFilters(CLEAR_REPORT_FILTERS)} />
          ) : (
            <OperationalTables report={operational.data} onFilter={applyFilters} />
          )}
        </TabsContent>

        {canPricing && (
          <TabsContent value="pricing" className="pt-3">
            {pricing.isLoading ? (
              <Skeleton className="h-64" />
            ) : pricing.error ? (
              <ErrorState error={pricing.error} onRetry={() => void pricing.refetch()} />
            ) : !pricing.data || pricing.data.rows.length === 0 ? (
              <ReportNoData
                filtered={filtered}
                onClear={() => applyFilters(CLEAR_REPORT_FILTERS)}
              />
            ) : (
              <PricingTables report={pricing.data} />
            )}
          </TabsContent>
        )}

        {allowed.providers && (
          <TabsContent value="providers" className="space-y-4 pt-3">
            {canProviders &&
              (providers.isLoading ? (
                <Skeleton className="h-48" />
              ) : providers.error ? (
                <ErrorState error={providers.error} onRetry={() => void providers.refetch()} />
              ) : !providers.data || providers.data.rows.length === 0 ? (
                <ReportNoData
                  filtered={filtered}
                  onClear={() => applyFilters(CLEAR_REPORT_FILTERS)}
                />
              ) : (
                <ProviderTables report={providers.data} />
              ))}
            {canCatalogue && (
              <CatalogueByCountry countries={allCountries || !country ? countries : [country]} />
            )}
          </TabsContent>
        )}

        <TabsContent value="countries" className="pt-3">
          {operational.isLoading ? (
            <Skeleton className="h-48" />
          ) : operational.error ? (
            <ErrorState error={operational.error} onRetry={() => void operational.refetch()} />
          ) : !operational.data || operational.data.rows.length === 0 ? (
            <ReportNoData filtered={filtered} onClear={() => applyFilters(CLEAR_REPORT_FILTERS)} />
          ) : (
            <CountryComparison
              operational={operational.data}
              financial={canFinancial ? (financial.data ?? null) : null}
            />
          )}
        </TabsContent>

        {allowed.financial && (
          <TabsContent value="financial" className="pt-3">
            <FinancialTab
              partnerOnly={partnerFinancial}
              view={financialView}
              onViewChange={(view) =>
                update({ [FINANCIAL_VIEW_PARAM]: view === 'summary' ? null : view })
              }
              params={params}
              scopeAll={allCountries}
              filtered={filtered}
              onFilter={applyFilters}
            />
          </TabsContent>
        )}
      </Tabs>
    </PageContainer>
  );
}
